import { LDClient } from 'launchdarkly-js-client-sdk';
import { EventObject, Typestate } from 'xstate';
import {
  AcknowledgeLeaseTermsError,
  AdditionalCustomerInfo,
  BankAccountInput,
  BillingAddress,
  CreateDraftAppErrors,
  CustomerEligibility,
  DebitDetailsInput,
  GenerateBankConnectorTokenErrors,
  GenerateContractErrors,
  GetExistingLeaseDocumentsErrors,
  HardDecline,
  LeaseDocuments,
  LeaseItem,
  Order,
  ProtectionPlusEnrollmentError,
  SelectLeaseOptionErrors,
  SigningErrors,
  SoftDecline,
  UpdateBankAccountErrors,
  UpdateCustomerErrors,
  UpdateDebitErrors,
  UpdateEmploymentErrors,
  VerifyEmailAddressErrors,
} from '../../gql/graphql';
import { ResolverError } from '../../constants/resolver-error';
import { SupportedLanguage } from '../../helpers/language/language.helper';
import { ApplyResult } from '../../networkRequests/apply/apply';
import { PayFrequencyModel } from '../../networkRequests/employment/updateEmployment';
import { PaymentSelectionErrors } from '../../pages/paymentSelectionError/PaymentSelectionError';
import { ReviewAndSubmitAlertTypes } from '../../pages/reviewAndSubmit/ReviewAndSubmitAlert';
import { BasicInfoContinuedModel, BasicInfoModel } from './ecommMachine.types';
import { ApplyRequestMachineContext, ApplyResponseMachineContext } from './shared/context/applyMachineContext';
import { BankDetailsMachineContext } from './shared/context/bankDetailsMachineContext';
import { CellPhoneMachineContext } from './shared/context/cellPhoneMachineContext';
import { OrderIdMachineContext } from './shared/context/orderIdMachineContext';
import { OrderMachineContext } from './shared/context/orderMachineContext';
import { RoleMachineContext } from './shared/context/roleMachineContext';

// Context Types
export interface LongFormMachineContext
  extends RoleMachineContext,
    OrderIdMachineContext,
    OrderMachineContext,
    CellPhoneMachineContext,
    ApplyRequestMachineContext,
    ApplyResponseMachineContext,
    BankDetailsMachineContext {
  // configs
  highestSupportedApplicationMilestone: number;

  // state managers
  transitionedFrom?: LongFormStates;
  skipToPage?: string;
  // handed to the parent on the PostDecision exit, which forwards it to post-decision-machine as
  // skipToPage. Only set where the status alone would route somewhere else.
  postDecisionStartingPage?: string;
  // set by the debit card update link on the post-decision contract summary; cleared on leaving DebitCardInformation
  fromContractSummary: boolean;
  isReadOnly?: boolean;
  contractSummaryPageParam: boolean;
  merchantContractSummaryPageParam: boolean;

  eligibilityDetails?: CustomerEligibility;

  // request responses and other long form data

  didSkipOTP: boolean;

  verifyEmailErrors?: VerifyEmailAddressErrors[];

  createDraftAppErrors?: CreateDraftAppErrors[];

  authorizeCardErrors?: UpdateDebitErrors[];

  additionalCustomerInfo: AdditionalCustomerInfo | undefined;

  plaidToken: {
    linkToken: string;
    publicToken: string;
  };
  bankConnection: {
    plaidLoading: boolean;
    paymentSelectionError?: PaymentSelectionErrors;
    isTokenizedAcct: boolean;
    bankAcctLast4: string;
  };

  updateEmploymentErrors?: UpdateEmploymentErrors[];
  debitInfo: DebitDetailsInput | undefined;
  updateDebitInfoErrors?: UpdateDebitErrors[];
  payFrequencyDetails: PayFrequencyModel | undefined;

  dismissibleAlerts: ReviewAndSubmitAlertTypes[];
  didCustomerChange: boolean;
  updateBasicInfoContinuedErrors?: UpdateCustomerErrors[];

  ldClient: LDClient;

  selectedLeaseTerm?: string; // comes from the selectLeaseOption resp. This is NOT defaulted to the selectedLeaseTerm application property
  customerSelectedLeaseTerm?: string;
  selectLeaseOptionErrors?: SelectLeaseOptionErrors[];
  acknowledgeLeaseTermsErrors?: AcknowledgeLeaseTermsError[];

  leaseDocuments?: LeaseDocuments;
  leaseDocumentsLanguage?: SupportedLanguage;
  leaseDocumentsByLanguage?: Partial<Record<SupportedLanguage, LeaseDocuments>>;
  /** Read-only only: whether the persisted unsigned English companion is on file for this lease. */
  hasEnglishLeaseCompanion?: boolean;
  generateContractErrors: GenerateContractErrors[];
  getExistingLeaseDocumentsErrors: GetExistingLeaseDocumentsErrors[];

  signingErrors: SigningErrors[];
  leaseSigned: boolean;
  loanSigned: boolean;

  applicationItems: LeaseItem[]; // make sure this is populated before you use it

  // OOBA Resend state for ContractSummaryBM
  showNotAcknowledgedModal?: boolean;
  protectionPlusEnrollmentErrors?: ProtectionPlusEnrollmentError[];
  autoVerticalError?: boolean;
}

// State Types
export enum LongFormStates {
  Entry = 'Entry',
  Select = 'Select', // top level state used for switching. All transitions are immediate so no page mapping needed in Flow page
  PersonalInformation = 'personalInformation',
  ContactInformation = 'contactInformation',
  AccommodationAddress = 'accommodationAddress',
  LTOIneligible = 'LTOIneligible',
  BankingInformation = 'bankingInformation',
  DebitCardInformation = 'debitCardInformation',
  DebitCardEntryInit = 'DebitCardEntryInit',
  UpdateDebitCard = 'UpdateDebitCard',
  ReviewAndSubmit = 'reviewAndSubmit',
  AmendAndResubmit = 'amendAndResubmit',
  PreApproval = 'preApproval',
  CompleteAppLater = 'completeAppLater',
  Declined = 'declined',
  InternalError = 'internalError',
  Ineligible = 'ineligible',
  Otb = 'otb',
  // exit: hands the session to post-decision-machine (OR-2997)
  PostDecision = 'PostDecision',

  //Income Source
  IncomeSource = 'incomeSource',
  UpdateEmployment = 'UpdateEmployment',

  //Payment Selection/Plaid States
  PaymentSelection = 'PaymentSelection',
  PaymentSelectionError = 'PaymentSelectionError',
  PlaidModal = 'PlaidModal',
  PaymentReview = 'PaymentReview',
  FetchLinkToken = 'FetchLinkToken',
  UpdateBankAccount = 'UpdateBankAccount',

  //Ecomm States
  Ecomm = 'Ecomm',

  // info pages
  PrequalificationNotice = 'PrequalificationNotice',

  PhoneNumberChanged = 'PhoneNumberChanged',
}

export interface LongFormMachineStatesSchema {
  [LongFormStates.Entry]: {};
  [LongFormStates.PersonalInformation]: {};
  [LongFormStates.ContactInformation]: {};
  [LongFormStates.AccommodationAddress]: {};
  [LongFormStates.LTOIneligible]: {};
  [LongFormStates.IncomeSource]: {};
  [LongFormStates.BankingInformation]: {};
  [LongFormStates.DebitCardInformation]: {};
  [LongFormStates.DebitCardEntryInit]: {};
  [LongFormStates.UpdateDebitCard]: {};
  [LongFormStates.ReviewAndSubmit]: {};
  [LongFormStates.AmendAndResubmit]: {};
  [LongFormStates.PrequalificationNotice]: {};
  [LongFormStates.PreApproval]: {};
  [LongFormStates.CompleteAppLater]: {};
  [LongFormStates.Declined]: {};
  [LongFormStates.InternalError]: {};
  [LongFormStates.Ineligible]: {};
  [LongFormStates.Otb]: {};
  [LongFormStates.PaymentSelection]: {};
  [LongFormStates.PaymentSelectionError]: {};
  [LongFormStates.PaymentReview]: {};
  [LongFormStates.PlaidModal]: {};
  [LongFormStates.FetchLinkToken]: {};
  [LongFormStates.UpdateEmployment]: {};
  [LongFormStates.FetchLinkToken]: {};
  [LongFormStates.Ecomm]: {};
  [LongFormStates.PhoneNumberChanged]: {};
  [LongFormStates.PostDecision]: {};
}

export interface LongFormMachineStates extends Typestate<LongFormMachineContext> {
  type: LongFormMachineStatesSchema;
}

// Event Types
export enum LongFormEvents {
  NEXT = 'NEXT',
  BACK = 'BACK',
  ERROR = 'ERROR',
  CancelButtonClicked = 'CancelButtonClicked',

  // info page events
  VIEW_PREQUALIFICATION_NOTICE = 'ViewPrequalificationNotice',

  // pre-approved page events
  COMPLETE_APP_LATER = 'CompleteAppLater',

  // Payment Selection/Plaid Events
  SELECT_PLAID = 'SelectPlaid',
  SELECT_MANUAL = 'SelectManual',
  FETCH_LINK_TOKEN = 'FetchLinkToken',
  PLAID_MODAL_LOADING = 'PlaidModalLoading',
  UPDATE_BANK_ACCOUNT = 'UpdateBankAccount',

  CONTACT_INFORMATION = 'ContactInformation',
  REVIEW_AND_SUBMIT = 'ReviewAndSubmit',
  PERSONAL_INFORMATION = 'PersonalInformation',
  ACCOMMODATION_ADDRESS = 'AccommodationAddress',
  INCOME_SOURCE = 'IncomeSource',
  PAYMENT_SELECTION = 'PaymentSelection',
  BANKING_INFORMATION = 'BankingInformation',
  DEBIT_CARD_INFORMATION = 'DebitCardInformation',
  UPDATE_DEBIT_CARD = 'UpdateDebitCard',

  SUBMIT_CONTACT_INFORMATION = 'SUBMIT_BASICS_CONTINUED',

  TRACK = 'TRACK',
  PAGE = 'PAGE',
  IDENTIFY = 'IDENTIFY',

  //Ecomm Specific events
  PREAPPROVED = 'Preapproved',
  CALL_AUTH_IP = 'CallAuthIP',
  // LEASE_SIGNED = 'LeaseSigned',
  EDIT_DEBIT_INFO = 'EditDebitInfo',
  APPROVED = 'Approved',
  DECLINED_OR_ERROR = 'DeclinedOrError',

  RESTART_APPLICATION = 'RestartApplication',
}

type LongFormEventsTypeSchema =
  | LongFormEvents.NEXT
  | LongFormEvents.BACK
  | LongFormEvents.ERROR
  | LongFormEvents.CancelButtonClicked
  | LongFormEvents.VIEW_PREQUALIFICATION_NOTICE
  | LongFormEvents.COMPLETE_APP_LATER
  | LongFormEvents.SELECT_PLAID
  | LongFormEvents.SELECT_MANUAL
  | LongFormEvents.FETCH_LINK_TOKEN
  | LongFormEvents.PLAID_MODAL_LOADING
  | LongFormEvents.CONTACT_INFORMATION
  | LongFormEvents.REVIEW_AND_SUBMIT
  | LongFormEvents.PERSONAL_INFORMATION
  | LongFormEvents.ACCOMMODATION_ADDRESS
  | LongFormEvents.INCOME_SOURCE
  | LongFormEvents.PAYMENT_SELECTION
  | LongFormEvents.BANKING_INFORMATION
  | LongFormEvents.DEBIT_CARD_INFORMATION
  | LongFormEvents.UPDATE_DEBIT_CARD
  | LongFormEvents.SUBMIT_CONTACT_INFORMATION
  | LongFormEvents.TRACK
  | LongFormEvents.PAGE
  | LongFormEvents.IDENTIFY
  | LongFormEvents.PREAPPROVED
  | LongFormEvents.CALL_AUTH_IP
  // | LongFormEvents.LEASE_SIGNED
  | LongFormEvents.EDIT_DEBIT_INFO
  | LongFormEvents.APPROVED
  | LongFormEvents.DECLINED_OR_ERROR
  | LongFormEvents.RESTART_APPLICATION;

export type LongFormEventsData = {
  target?: LongFormStates;
  softDeclineError?: SoftDecline;
  hardDeclineError?: HardDecline;
  eligibilityDetails?: CustomerEligibility;
  resolverErrors?: ResolverError[];
  isCurrentApplyTypeOTB?: boolean;
  updateBankAccountErrors?: UpdateBankAccountErrors[];
  generateBankConnectorTokenErrors?: GenerateBankConnectorTokenErrors[];
  publicToken?: string;
  order?: Order;
  debitDetails?: DebitDetailsInput;
  billingAddress?: BillingAddress;
  bankAcctLast4: string; // TODO is this right?
  createDraftAppErrors?: CreateDraftAppErrors[];

  language?: SupportedLanguage;

  // for queryCustomersByIDV
  taxIdLast4: string;
  basicInfoContinued: BasicInfoContinuedModel;
  didCustomerChange: boolean;
} & ApplyResult &
  BasicInfoModel &
  BankAccountInput &
  PayFrequencyModel;

export interface LongFormMachineEvents extends EventObject {
  type: LongFormEventsTypeSchema;
  data?: LongFormEventsData;
}

export enum LongFormGuardsType {
  LEASE_DECLINED = 'LeaseDecline',
  LOAN_DECLINED = 'LoanDecline',
  LTO_INELIGIBLE = 'LTOIneligible',
  APP_APPROVED = 'AppApproved',
  GENERAL_APPLY_ERRORS = 'GeneralApplyErrors',
  SOFT_DECLINE = 'SoftDecline',
}
type LongFormGuardsTypeSchema =
  | LongFormGuardsType.LEASE_DECLINED
  | LongFormGuardsType.LOAN_DECLINED
  | LongFormGuardsType.LTO_INELIGIBLE
  | LongFormGuardsType.GENERAL_APPLY_ERRORS
  | LongFormGuardsType.SOFT_DECLINE
  | LongFormGuardsType.APP_APPROVED;

export interface LongFormMachineGuards extends EventObject {
  type: LongFormGuardsTypeSchema;
}
